import { ref } from 'vue';
import { WEB_SOCKET_URL, BASE_URL, DOWNLOAD_PARSED_PDF_API_ENDPOINT } from "@/assets/constants/api_endpoints";
import { WS_MESSAGE_DOCUMENT_CONTENT, WS_MESSAGE_ERROR, WS_MESSAGE_VOCABULARY_DEFINITION, LOAD_PDF_FAILED_ERR_MSG, FAILED_STATUS } from "@/assets/constants/constants.js";
import { REQUEST_TYPE_SENTENCE_ANALYSIS, REQUEST_TYPE_LOAD_DOCUMENT } from "@/assets/constants/constants.js";
import axios from "axios";

/**
 * Composable for handling WebSocket communication
 * Manages socket connection lifecycle and message handling
 */
export function useSocketCommunication() {
    const socket = ref(null);

    const initializeSocket = (authToken = null) => {
        let url = WEB_SOCKET_URL;
        if (authToken) {
            // Append auth token as query parameter for WebSocket authentication
            const separator = WEB_SOCKET_URL.includes('?') ? '&' : '?';
            url = `${WEB_SOCKET_URL}${separator}token=${authToken}`;
        }
        socket.value = new WebSocket(url);
    };

    const setupSocketHandlers = (onMessage, onError, onClose) => {
        if (!socket.value) return;

        socket.value.onmessage = (event) => {
            const messageInfo = JSON.parse(event.data);
            if (onMessage) onMessage(messageInfo);
        };

        socket.value.onerror = (error) => {
            if (onError) onError(error);
        };

        socket.value.onclose = () => {
            if (onClose) onClose();
        };
    };

    const sendMessage = (payload) => {
        if (socket.value && socket.value.readyState === WebSocket.OPEN) {
            socket.value.send(JSON.stringify(payload));
            return true;
        }
        return false;
    };

    const sendAnalysisRequest = (text) => {
        return sendMessage({
            request_type: REQUEST_TYPE_SENTENCE_ANALYSIS,
            sentence: text
        });
    };

    const sendLoadDocumentRequest = (resourceInfo) => {
        const payload = { ...resourceInfo, request_type: REQUEST_TYPE_LOAD_DOCUMENT };
        return sendMessage(payload);
    };

    const sendUploadRequest = (uploadData) => {
        return sendMessage(uploadData);
    };

    const closeSocket = () => {
        if (socket.value) {
            socket.value.close();
            socket.value = null;
        }
    };

    const isSocketOpen = () => {
        return socket.value && socket.value.readyState === WebSocket.OPEN;
    };

    return {
        socket,
        initializeSocket,
        setupSocketHandlers,
        sendMessage,
        sendAnalysisRequest,
        sendLoadDocumentRequest,
        sendUploadRequest,
        closeSocket,
        isSocketOpen,
        WS_MESSAGE_DOCUMENT_CONTENT,
        WS_MESSAGE_ERROR,
        WS_MESSAGE_VOCABULARY_DEFINITION,
    };
}
